import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  IconArrowsMove,
  IconAugmentedReality,
  IconKeyboard,
  IconMaximize,
  IconPalette,
  IconPlayerPlay,
  IconQrcode,
  IconRectangle,
  IconRotate360,
  IconShare,
  IconStar,
  IconView360,
  IconX,
  IconZoomIn,
  IconZoomReset,
  IconPointer,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getShortcutModKeyLabel } from '@/utils/keyboardShortcuts';
import classes from './ModelViewerHelpOverlay.module.css';

export type ModelViewerHelpShortcutFlags = {
  save?: boolean;
  annotationMode?: boolean;
  annotationStep?: boolean;
  animationStep?: boolean;
  resetView?: boolean;
  help?: boolean;
  playPause?: boolean;
  escape?: boolean;
};

export type ModelViewerHelpFeatures = {
  resetView?: boolean;
  appearance?: boolean;
  annotations?: boolean;
  /** Editor: place/edit annotations (different copy from browse annotations). */
  annotationEdit?: boolean;
  animations?: boolean;
  ar?: boolean;
  theater?: boolean;
  fullscreen?: boolean;
  qr?: boolean;
  share?: boolean;
  favorites?: boolean;
  /** When set, shows the Shortcuts tab with the enabled keys. */
  shortcuts?: ModelViewerHelpShortcutFlags;
};

type ModelViewerHelpOverlayProps = {
  opened: boolean;
  onClose: () => void;
  onResetCamera: () => void;
  features?: ModelViewerHelpFeatures;
  zIndex?: number;
};

type HelpTab = 'navigation' | 'controls' | 'shortcuts';

type ControlRow = {
  key: string;
  icon: ReactNode;
  title: string;
  description: string;
};

type ShortcutRow = {
  key: string;
  keys: string[];
  /** How to join multiple key labels in the UI. */
  joinWith?: 'plus' | 'or';
  title: string;
  description: string;
};

export function ModelViewerHelpOverlay({
  opened,
  onClose,
  onResetCamera,
  features = {},
  zIndex = 100,
}: ModelViewerHelpOverlayProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<HelpTab>('navigation');
  const modKey = useMemo(() => getShortcutModKeyLabel(), []);

  useEffect(() => {
    if (!opened) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [opened, onClose]);

  useEffect(() => {
    if (opened) {
      setActiveTab('navigation');
    }
  }, [opened]);

  const controlRows = useMemo(() => {
    const rows: ControlRow[] = [];

    if (features.resetView) {
      rows.push({
        key: 'resetView',
        icon: <IconZoomReset size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.resetView.title'),
        description: t('modelViewer.help.controls.resetView.desc'),
      });
    }
    if (features.appearance) {
      rows.push({
        key: 'appearance',
        icon: <IconPalette size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.appearance.title'),
        description: t('modelViewer.help.controls.appearance.desc'),
      });
    }
    if (features.annotationEdit) {
      rows.push({
        key: 'annotationEdit',
        icon: <IconPointer size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.annotationEdit.title'),
        description: t('modelViewer.help.controls.annotationEdit.desc'),
      });
    } else if (features.annotations) {
      rows.push({
        key: 'annotations',
        icon: <IconView360 size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.annotations.title'),
        description: t('modelViewer.help.controls.annotations.desc'),
      });
    }
    if (features.animations) {
      rows.push({
        key: 'animations',
        icon: <IconPlayerPlay size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.animations.title'),
        description: t('modelViewer.help.controls.animations.desc'),
      });
    }
    if (features.ar) {
      rows.push({
        key: 'ar',
        icon: <IconAugmentedReality size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.ar.title'),
        description: t('modelViewer.help.controls.ar.desc'),
      });
    }
    if (features.theater) {
      rows.push({
        key: 'theater',
        icon: <IconRectangle size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.theater.title'),
        description: t('modelViewer.help.controls.theater.desc'),
      });
    }
    if (features.fullscreen) {
      rows.push({
        key: 'fullscreen',
        icon: <IconMaximize size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.fullscreen.title'),
        description: t('modelViewer.help.controls.fullscreen.desc'),
      });
    }
    if (features.qr) {
      rows.push({
        key: 'qr',
        icon: <IconQrcode size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.qr.title'),
        description: t('modelViewer.help.controls.qr.desc'),
      });
    }
    if (features.share) {
      rows.push({
        key: 'share',
        icon: <IconShare size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.share.title'),
        description: t('modelViewer.help.controls.share.desc'),
      });
    }
    if (features.favorites) {
      rows.push({
        key: 'favorites',
        icon: <IconStar size={22} stroke={1.75} />,
        title: t('modelViewer.help.controls.favorites.title'),
        description: t('modelViewer.help.controls.favorites.desc'),
      });
    }

    return rows;
  }, [features, t]);

  const shortcutRows = useMemo(() => {
    const flags = features.shortcuts;
    if (!flags) return [];

    const rows: ShortcutRow[] = [];
    if (flags.save) {
      rows.push({
        key: 'save',
        keys: [modKey, 'S'],
        joinWith: 'plus',
        title: t('modelViewer.help.shortcuts.save.title'),
        description: t('modelViewer.help.shortcuts.save.desc'),
      });
    }
    if (flags.annotationMode) {
      rows.push({
        key: 'annotationMode',
        keys: ['A'],
        title: t('modelViewer.help.shortcuts.annotationMode.title'),
        description: t('modelViewer.help.shortcuts.annotationMode.desc'),
      });
    }
    if (flags.annotationStep) {
      rows.push({
        key: 'annotationStep',
        keys: ['←', '→'],
        joinWith: 'or',
        title: t('modelViewer.help.shortcuts.annotationStep.title'),
        description: t('modelViewer.help.shortcuts.annotationStep.desc'),
      });
    }
    if (flags.animationStep) {
      rows.push({
        key: 'animationStep',
        keys: ['[', ']'],
        joinWith: 'or',
        title: t('modelViewer.help.shortcuts.animationStep.title'),
        description: t('modelViewer.help.shortcuts.animationStep.desc'),
      });
    }
    if (flags.resetView) {
      rows.push({
        key: 'resetView',
        keys: ['R'],
        title: t('modelViewer.help.shortcuts.resetView.title'),
        description: t('modelViewer.help.shortcuts.resetView.desc'),
      });
    }
    if (flags.playPause) {
      rows.push({
        key: 'playPause',
        keys: ['Space'],
        title: t('modelViewer.help.shortcuts.playPause.title'),
        description: t('modelViewer.help.shortcuts.playPause.desc'),
      });
    }
    if (flags.help) {
      rows.push({
        key: 'help',
        keys: ['?', 'H'],
        joinWith: 'or',
        title: t('modelViewer.help.shortcuts.help.title'),
        description: t('modelViewer.help.shortcuts.help.desc'),
      });
    }
    if (flags.escape) {
      rows.push({
        key: 'escape',
        keys: ['Esc'],
        title: t('modelViewer.help.shortcuts.escape.title'),
        description: t('modelViewer.help.shortcuts.escape.desc'),
      });
    }
    return rows;
  }, [features.shortcuts, modKey, t]);

  if (!opened) {
    return null;
  }

  const showControlsTab = controlRows.length > 0;
  const showShortcutsTab = shortcutRows.length > 0;

  return (
    <div
      className={classes.overlay}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={t('modelViewer.help.button')}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={classes.header}>
        <div className={classes.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'navigation'}
            className={`${classes.tab} ${activeTab === 'navigation' ? classes.tabActive : ''}`}
            onClick={() => setActiveTab('navigation')}
          >
            {t('modelViewer.help.tabNavigation')}
          </button>
          {showControlsTab && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'controls'}
              className={`${classes.tab} ${activeTab === 'controls' ? classes.tabActive : ''}`}
              onClick={() => setActiveTab('controls')}
            >
              {t('modelViewer.help.tabAllControls')}
            </button>
          )}
          {showShortcutsTab && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'shortcuts'}
              className={`${classes.tab} ${activeTab === 'shortcuts' ? classes.tabActive : ''}`}
              onClick={() => setActiveTab('shortcuts')}
            >
              {t('modelViewer.help.tabShortcuts')}
            </button>
          )}
        </div>

        <button
          type="button"
          className={classes.closeButton}
          aria-label={t('modelViewer.help.close')}
          onClick={onClose}
        >
          <IconX size={22} stroke={2} />
        </button>
      </div>

      <div className={classes.content}>
        {activeTab === 'navigation' ? (
          <div className={classes.navigationGrid}>
            <div className={classes.navigationItem}>
              <div className={classes.navigationIcon}>
                <IconRotate360 size={48} stroke={1.5} />
              </div>
              <h3 className={classes.navigationTitle}>{t('modelViewer.help.orbitTitle')}</h3>
              <p className={classes.navigationDesc}>{t('modelViewer.help.orbitDesc')}</p>
            </div>

            <div className={classes.navigationItem}>
              <div className={classes.navigationIcon}>
                <IconZoomIn size={48} stroke={1.5} />
              </div>
              <h3 className={classes.navigationTitle}>{t('modelViewer.help.zoomTitle')}</h3>
              <p className={classes.navigationDesc}>{t('modelViewer.help.zoomDesc')}</p>
            </div>

            <div className={classes.navigationItem}>
              <div className={classes.navigationIcon}>
                <IconArrowsMove size={48} stroke={1.5} />
              </div>
              <h3 className={classes.navigationTitle}>{t('modelViewer.help.panTitle')}</h3>
              <p className={classes.navigationDesc}>{t('modelViewer.help.panDesc')}</p>
            </div>
          </div>
        ) : activeTab === 'shortcuts' ? (
          <div className={classes.shortcutsList}>
            {shortcutRows.map((row) => (
              <div key={row.key} className={classes.shortcutItem}>
                <div className={classes.shortcutKeys} aria-hidden>
                  {row.keys.map((keyLabel, index) => (
                    <span key={`${row.key}-${keyLabel}-${index}`} className={classes.shortcutKeyGroup}>
                      {index > 0 ? (
                        <span className={classes.shortcutOr}>
                          {row.joinWith === 'or'
                            ? t('modelViewer.help.shortcutOr')
                            : '+'}
                        </span>
                      ) : null}
                      <kbd className={classes.shortcutKbd}>{keyLabel}</kbd>
                    </span>
                  ))}
                </div>
                <div className={classes.shortcutText}>
                  <p className={classes.controlTitle}>{row.title}</p>
                  <p className={classes.controlDesc}>{row.description}</p>
                </div>
              </div>
            ))}
            <div className={classes.shortcutHint}>
              <IconKeyboard size={16} stroke={1.75} />
              <span>{t('modelViewer.help.shortcutsHint')}</span>
            </div>
          </div>
        ) : (
          <div className={classes.controlsGrid}>
            {controlRows.map((row) => (
              <div key={row.key} className={classes.controlItem}>
                <div className={classes.controlIcon}>{row.icon}</div>
                <div>
                  <p className={classes.controlTitle}>{row.title}</p>
                  <p className={classes.controlDesc}>{row.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <button
          type="button"
          className={classes.resetButton}
          onClick={() => {
            onResetCamera();
            onClose();
          }}
        >
          {t('modelViewer.help.resetCamera')}
        </button>
      </div>
    </div>
  );
}

